import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider, useSite } from "@/context/SiteContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";
import Portfolio from "@/pages/Portfolio";
import ProjectPage from "@/pages/ProjectPage";
import Privacy from "@/pages/Privacy";
import OfferBanner from "@/components/site/OfferBanner";
import { api } from "@/lib/api";
import { captureSource } from "@/lib/refSource";
import ReviewPage from "@/pages/ReviewPage";
import "@/advisor/advisor.css";
import { initPixel, metaPageView } from "@/lib/meta";
import { AdvisorProvider } from "@/advisor/AdvisorContext";
import AdvisorLauncher from "@/advisor/AdvisorLauncher";
import { StickyProjectBar } from "@/advisor/StickyProjectBar";
import AdvisorPage from "@/advisor/AdvisorPage";
import AdvisorProject from "@/advisor/Project";
import AdvisorQuote from "@/advisor/Quote";
import AdvisorSuccess from "@/advisor/Success";

const GameModal = lazy(() => import("@/components/game/GameModal"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 80);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function useVisit() {
  useEffect(() => {
    captureSource();
    if (sessionStorage.getItem("lm_visit")) return;
    api.post("/visit", { path: window.location.pathname, ref: document.referrer || "", campaign: new URLSearchParams(window.location.search).get("utm_campaign")?.slice(0, 60) || "" }).then(() => sessionStorage.setItem("lm_visit", "1")).catch(() => {});
  }, []);
}

function MetaPixel() {
  const { config } = useSite();
  const { pathname } = useLocation();
  const id = config?.meta_pixel_id;
  useEffect(() => {
    if (!id) return;
    initPixel(id);
    metaPageView();
  }, [id, pathname]);
  return null;
}

function PublicLayout() {
  const { game } = useSite();
  useVisit();
  return (
    <>
      <MetaPixel />
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
      <OfferBanner />
      <AdvisorLauncher />
      <StickyProjectBar />
      {game && (
        <Suspense fallback={null}>
          <GameModal key={game.key} mode={game.mode} />
        </Suspense>
      )}
    </>
  );
}

function App() {
  return (
    <SiteProvider>
      <BrowserRouter>
        <AdvisorProvider>
        <ScrollManager />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/layiheler" element={<Portfolio />} />
            <Route path="/layihe/:slug" element={<ProjectPage />} />
            <Route path="/mexfilik" element={<Privacy />} />
            <Route path="/rey/:token" element={<ReviewPage />} />
            <Route path="/advisor" element={<AdvisorPage />} />
            <Route path="/advisor/layihe" element={<AdvisorProject />} />
            <Route path="/advisor/teklif" element={<AdvisorQuote />} />
            <Route path="/advisor/ugur" element={<AdvisorSuccess />} />
          </Route>
          <Route path="/admin/login" element={<Suspense fallback={null}><AdminLogin /></Suspense>} />
          <Route path="/admin" element={<Suspense fallback={null}><AdminDashboard /></Suspense>} />
        </Routes>
        </AdvisorProvider>
      </BrowserRouter>
      <Toaster theme="light" position="top-center" />
    </SiteProvider>
  );
}

export default App;
